import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import DocumentationsPage from "pages/DocumentationsPage";
import DocumentationPage from "pages/DocumentationPage";
import DocumentationViewModePage from "pages/DocumentationViewModePage";
import ProgressReportPage from "pages/ProgressReportPage";
import ProgressReportViewModePage from "pages/ProgressReportViewModePage";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ToastContainer } from "react-toastify";
import { Login } from "./components/auth/Login";
import { Profile } from "./components/Profile";
import { Register } from "./components/auth/Register";
import { ForgotPasswordPage } from "./components/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import DashboardMonitoring from "./components/DashboardMonitoringPage/DashboardMonitoring";
import { DashboardPage } from "./components/DashboardPage";
import DemoProjectDetails from "./components/DemoProjectDetails";
import GlobalAdminDashboard from "./components/GlobalAdminDashboard";
import OrganizationManagement from "./components/OrganizationManagement";
import ProjectDetails from "./components/ProjectDetails";
import ProjectWizard from "./components/ProjectWizard";
import ProtectedRoute from "./components/ProtectedRoute";
import RouteChangeTracker from "./components/RouteChangeTracker";
import { AllCircuits } from "./components/AllCircuits";
import { ReviewPageWrapperV3 } from "./components/SmartSurveyor/SmartSurveyorV3/ReviewPageWrapperV3";
import { ResearchReportPage } from "./components/SmartSurveyor/SmartSurveyorV3/ResearchReportPage/ResearchReportPage";
import { ResearchChatPageWrapper } from "./components/SmartSurveyor/SmartSurveyorV3/ResearchReportPage/ResearchChatPage/ResearchChatPageWrapper";
import { OrganizationGuard } from "./components/OrganizationGuard";
// Legacy DWG demo entry — preserved on disk but no longer mounted.
// import { SmartSurveyor } from "components/SmartSurveyor";
import { RudolfFritzSmartSurveyorDataUploader } from "./components/SmartSurveyor/SmartSurveyorForRudolfFritz/RudolfFritzSmartSurveyorDataUploader";
import { AuthProvider } from "./contexts/AuthContext";
import AuthedLayout from "./layouts/AuthedLayout";
import "./polyfills";
import { initAnalytics } from "./utils/analytics";

dayjs.extend(utc);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Initialize Amplitude Analytics on app load
  useEffect(() => {
    initAnalytics();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route
              path="/login"
              element={
                <>
                  <Login />
                  <RouteChangeTracker />
                </>
              }
            />
            <Route
              path="/invite/:token"
              element={
                <>
                  <Register />
                  <RouteChangeTracker />
                </>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <>
                  <ForgotPasswordPage />
                  <RouteChangeTracker />
                </>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <>
                  <ResetPasswordPage />
                  <RouteChangeTracker />
                </>
              }
            />
            <Route
              path="project/:id/sections/:sectionId/documentation/:documentationId/view"
              element={<DocumentationViewModePage />}
            />
            <Route
              path="project/:id/sections/:sectionId/progress-report/view"
              element={<ProgressReportViewModePage />}
            />
            <Route element={<ProtectedRoute />}>
              <Route element={<OrganizationGuard />}>
                {/* Legacy DWG demo route — preserved in code but no longer mounted. */}
                {/* <Route
                  path="project/smart-surveyor"
                  element={<SmartSurveyor />}
                /> */}
                <Route
                  path="project/:id/smart-surveyor/research"
                  element={<ResearchReportPage />}
                />
                <Route
                  path="project/:id/smart-surveyor/research/chat"
                  element={<ResearchChatPageWrapper />}
                />
                <Route
                  path="project/:id/smart-surveyor"
                  element={<ReviewPageWrapperV3 />}
                />
                <Route
                  path="/"
                  element={
                    <>
                      <AuthedLayout />
                      <RouteChangeTracker />
                    </>
                  }
                >
                  <Route index element={<DashboardPage />} />
                  <Route
                    path="project/:id/smart-surveyor/upload"
                    element={<RudolfFritzSmartSurveyorDataUploader />}
                  />
                  <Route
                    path="project/all-circuits"
                    element={<AllCircuits />}
                  />
                  <Route path="project/new" element={<ProjectWizard />} />
                  <Route path="project/demo" element={<DemoProjectDetails />} />
                  <Route
                    path="project/:id"
                    element={<Navigate to="sections" replace />}
                  />
                  <Route
                    path="project/:id/sections"
                    element={<ProjectDetails />}
                  />
                  <Route
                    path="project/:id/sections/:sectionId"
                    element={<ProjectDetails />}
                  />
                  <Route
                    path="project/:id/sections/:sectionId/documentation"
                    element={<ProjectDetails />}
                  />
                  <Route
                    path="project/:id/billofquantities"
                    element={<ProjectDetails />}
                  />
                  <Route path="project/:id/team" element={<ProjectDetails />} />
                  <Route
                    path="project/:id/sections/:sectionId/documentation/:documentationId"
                    element={<DocumentationPage />}
                  />
                  <Route
                    path="project/:id/sections/:sectionId/progress-report"
                    element={<ProgressReportPage />}
                  />
                  <Route path="profile" element={<Profile />} />
                  <Route
                    path="organization"
                    element={<OrganizationManagement />}
                  />
                  <Route path="admin" element={<GlobalAdminDashboard />} />
                  <Route
                    path="project/:id/sections/:sectionId/dashboard"
                    element={<DashboardMonitoring />}
                  />
                  <Route
                    path="project/:id/documentations"
                    element={<DocumentationsPage />}
                  />
                </Route>
              </Route>
            </Route>
          </Routes>
        </Router>
        <ToastContainer position={"top-right"} />
      </AuthProvider>
      {process.env.NODE_ENV === "development" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
